import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Upload, X } from 'lucide-react';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { formatDatetime } from '../utils/dates';
import {
  getImportedBookings, deleteImportedBooking, assignBookingVehicle,
  getAllBookingBalances, setBookingCancelled,
} from '../api';
import type { ImportedBooking, BookingBalance } from '../api';
import VehicleSearchField from '../components/VehicleSearchField';
import Pagination from '../components/Pagination';
import ManualBookingModal from '../components/ManualBookingModal';
import ImportModal from '../components/ImportModal';
import BookingDetailModal from '../components/BookingDetailModal';
import { PageHeader, Button } from '../components/ui';

const N = (v: number | null | undefined) => Number(v ?? 0);

// Sum af komponenter − payout. Andet end 0 betyder at fakturaens regnestykke ikke går op
// (scraper-fejl eller et gebyr der mangler) — vises som rød prik.
function invoiceDiff(inv: ImportedBooking) {
  const sum =
    N(inv.rental_amount) + N(inv.dynamic_pricing) + N(inv.km_extra_amount)
    + N(inv.km_overage_amount) + N(inv.excess_reduction) + N(inv.late_return_fee)
    + N(inv.fuel_adjustment) + N(inv.foreign_driving_fee) + N(inv.extra_driver_fee)
    - N(inv.handling_fees) - N(inv.discount);
  return { sum, diff: Math.round((sum - N(inv.payout)) * 100) / 100 };
}

// Hvor langt en udlejning er nået i afstemningen. Alt andet end 'settled' er "ikke afsluttet".
//   not_invoiced — ingen faktura endnu: lejen er i gang/kommende, eller fakturaen er ikke hentet
//   mismatch     — regnestykket går ikke op, eller det modtagne afviger uden at bookingens
//                  ubetalte poster forklarer det
//   unpaid       — faktureret, men intet modtaget endnu
//   pending      — mangler præcis bookingens ubetalte poster; venter på afregning
type SettlementState = 'settled' | 'not_invoiced' | 'mismatch' | 'unpaid' | 'pending';

function settlementState(inv: ImportedBooking, bal: BookingBalance | undefined): SettlementState {
  if (inv.invoice_number == null) return 'not_invoiced';
  if (Math.abs(invoiceDiff(inv).diff) >= 0.01) return 'mismatch';
  if (!bal || bal.received === 0) return 'unpaid';
  if (bal.status === 'ok') return 'settled';
  if (bal.status === 'pending') return 'pending';
  return 'mismatch';
}

const hasEnded = (inv: ImportedBooking) =>
  !!inv.rental_end && new Date(inv.rental_end.replace(' ', 'T')) <= new Date();

type ReconcileFilter = '' | 'open' | 'mismatch' | 'pending' | 'unpaid' | 'not_invoiced';

export default function BookingsPage() {
  const { t } = useTranslation();
  const { settings } = useSettings();

  const [invoices, setInvoices] = useState<ImportedBooking[]>([]);
  const [balances, setBalances] = useState<Record<number, BookingBalance>>({});
  const [gmLoading, setGmLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [confirmDeleteGm, setConfirmDeleteGm] = useState<number | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<number | null>(null);
  const [viewingInvoice, setViewingInvoice] = useState<ImportedBooking | null>(null);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [filterReg, setFilterReg]     = useState('');
  const [filterRenter, setFilterRenter] = useState('');
  const [filterYear, setFilterYear]   = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [reconcile, setReconcile] = useState<ReconcileFilter>('');
  const [rentalsPage, setRentalsPage] = useState(1);

  const loadInvoices = useCallback(async () => {
    setGmLoading(true);
    const [invs, bals] = await Promise.all([getImportedBookings(), getAllBookingBalances()]);
    setInvoices(invs);
    setBalances(bals);
    setGmLoading(false);
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);
  useEffect(() => { setRentalsPage(1); }, [filterReg, filterRenter, filterYear, filterMonth, reconcile]);

  const handleAssignVehicle = async (invoiceId: number, vehicleId: number | null) => {
    await assignBookingVehicle(invoiceId, vehicleId);
    setAssigningId(null);
    loadInvoices();
  };

  const handleDeleteInvoice = async (id: number) => {
    await deleteImportedBooking(id);
    setConfirmDeleteGm(null);
    loadInvoices();
  };

  const handleMarkCancelled = async (id: number) => {
    await setBookingCancelled(id, true);
    setConfirmCancelId(null);
    loadInvoices();
  };

  const fmt = (n: number) => formatNumber(n, settings.numberFormat);

  const importedInvoices = invoices.filter(i => {
    if (i.cancelled && reconcile) return false;  // aflyste afstemmes ikke
    const state = settlementState(i, balances[i.id]);
    // Udlejninger uden faktura vises kun når der filtreres på dem — ellers er listen fakturaerne
    if (state === 'not_invoiced' && reconcile !== 'open' && reconcile !== 'not_invoiced') return false;
    if (filterReg && !(i.registration_number ?? '').toUpperCase().includes(filterReg.toUpperCase())) return false;
    // Alle ord skal findes i navnet, i vilkårlig rækkefølge — "emma joh" finder Emma Johansen
    if (filterRenter) {
      const name = (i.renter_name ?? '').toLocaleLowerCase('da');
      if (!filterRenter.toLocaleLowerCase('da').split(/\s+/).filter(Boolean).every(w => name.includes(w))) return false;
    }
    const dateStr = i.invoice_date ?? i.rental_start ?? '';
    if (filterYear  && !dateStr.startsWith(filterYear)) return false;
    if (filterMonth && dateStr.slice(5, 7) !== filterMonth) return false;
    if (reconcile === 'open' && state === 'settled') return false;
    if (reconcile && reconcile !== 'open' && state !== reconcile) return false;
    return true;
  });

  const rentalsPageSize = settings.itemsPerPage;
  const rentalsTotalPages = Math.ceil(importedInvoices.length / rentalsPageSize);
  const pagedInvoices = importedInvoices.slice((rentalsPage - 1) * rentalsPageSize, rentalsPage * rentalsPageSize);

  const availableYears = [...new Set(
    invoices
      .filter(i => i.invoice_number != null)
      .map(i => (i.invoice_date ?? i.rental_start ?? '').slice(0, 4))
      .filter(Boolean)
  )].sort((a, b) => b.localeCompare(a));

  const MONTH_KEYS = ['01','02','03','04','05','06','07','08','09','10','11','12'];

  const vatRate = settings.vatRate;
  const gmTotalPayout = importedInvoices.reduce((s, i) => s + Number(i.payout), 0);
  const gmTotalSalesVat = importedInvoices.reduce((s, i) => {
    const taxable = Number(i.rental_amount    ?? 0)
                  + Number(i.dynamic_pricing  ?? 0)
                  + Number(i.km_extra_amount  ?? 0)
                  + Number(i.km_overage_amount ?? 0)
                  + Number(i.fuel_adjustment  ?? 0)
                  + Number(i.late_return_fee  ?? 0)
                  - Number(i.discount         ?? 0);
    return s + taxable * vatRate / (100 + vatRate);
  }, 0);
  const gmTotalPurchaseVat = importedInvoices.reduce((s, i) => s + Number(i.handling_fees ?? 0) * vatRate / (100 + vatRate), 0);

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <PageHeader
        eyebrow={t('nav.fleet_group')}
        title={t('nav.bookings')}
        subtitle={`${importedInvoices.length} ${t('rentals.count')}`}
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Plus size={14} />}
              onClick={() => setShowManualModal(true)}
            >
              {t('manual_booking.btn')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              iconLeft={<Upload size={14} />}
              onClick={() => setShowImportModal(true)}
            >
              {t('rentals.import_btn')}
            </Button>
          </>
        }
      />

      {/* Filtre */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-ink-500 font-medium">{t('rentals.filter_plate')}:</span>
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={filterReg}
              onChange={e => setFilterReg(e.target.value.toUpperCase())}
              placeholder={t('rentals.filter_plate_placeholder')}
              className="filter-input w-32 uppercase placeholder:normal-case placeholder:font-sans font-mono"
            />
            {filterReg && (
              <button onClick={() => setFilterReg('')} className="text-ink-300 hover:text-ink-700 p-1 rounded hover:bg-cream-100">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        <div className="w-px h-4 bg-ink-100 mx-1" />

        <div className="flex items-center gap-1.5">
          <span className="text-xs text-ink-500 font-medium">{t('rentals.filter_renter')}:</span>
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={filterRenter}
              onChange={e => setFilterRenter(e.target.value)}
              placeholder={t('rentals.filter_renter_placeholder')}
              className="filter-input w-40"
            />
            {filterRenter && (
              <button onClick={() => setFilterRenter('')} className="text-ink-300 hover:text-ink-700 p-1 rounded hover:bg-cream-100">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        <div className="w-px h-4 bg-ink-100 mx-1" />

        <div className="flex items-center gap-1.5">
          <span className="text-xs text-ink-500 font-medium">{t('rentals.filter_year')}:</span>
          <select
            value={filterYear}
            onChange={e => { setFilterYear(e.target.value); setFilterMonth(''); }}
            className="filter-input"
          >
            <option value="">{t('rentals.filter_all')}</option>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {filterYear && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-ink-500 font-medium">{t('rentals.filter_month')}:</span>
            <select
              value={filterMonth}
              onChange={e => setFilterMonth(e.target.value)}
              className="filter-input"
            >
              <option value="">{t('rentals.filter_all')}</option>
              {MONTH_KEYS.map(k => (
                <option key={k} value={k}>{t(`months.${k}`)}</option>
              ))}
            </select>
          </div>
        )}

        <div className="w-px h-4 bg-ink-100 mx-1" />

        <div className="flex items-center gap-1.5">
          <span className="text-xs text-ink-500 font-medium">{t('rentals.filter_reconcile')}:</span>
          <select
            value={reconcile}
            onChange={e => setReconcile(e.target.value as typeof reconcile)}
            className="filter-input"
          >
            <option value="">{t('rentals.filter_all')}</option>
            <option value="open">{t('rentals.filter_open')}</option>
            <option value="mismatch">{t('rentals.filter_mismatch')}</option>
            <option value="pending">{t('rentals.filter_pending')}</option>
            <option value="unpaid">{t('rentals.filter_unpaid')}</option>
            <option value="not_invoiced">{t('rentals.filter_not_invoiced')}</option>
          </select>
        </div>

        {(filterReg || filterRenter || filterYear || filterMonth || reconcile) && (
          <button
            onClick={() => { setFilterReg(''); setFilterRenter(''); setFilterYear(''); setFilterMonth(''); setReconcile(''); }}
            className="text-xs text-ink-500 hover:text-ink-900 ml-1"
          >
            {t('rentals.filter_clear')}
          </button>
        )}
      </div>

      {/* Tabel-card */}
      <div className="list-card">
        {gmLoading ? (
          <div className="p-12 text-center text-ink-500">…</div>
        ) : importedInvoices.length === 0 ? (
          <div className="p-12 text-center text-ink-500">{t('rentals.none')}</div>
        ) : (
          <table className="data-table table-fixed">
            <thead>
              <tr>
                <th className="th w-[110px]">{t('rentals.col_vehicle')}</th>
                <th className="th w-[180px]">{t('rentals.col_period')}</th>
                <th className="th">{t('rentals.col_renter')}</th>
                <th className="th !text-right w-[110px]">{t('rentals.col_payout')}</th>
                <th className="th !text-center w-[40px]"></th>
                <th className="th !text-center w-[170px]">Modtaget</th>
                <th className="px-4 py-3 w-[50px]"></th>
              </tr>
            </thead>
            <tbody>
              {pagedInvoices.map(inv => (
                <tr key={inv.id} onClick={() => setViewingInvoice(inv)} className="row-hover cursor-pointer">
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {assigningId === inv.id ? (
                      <div className="flex items-center gap-1">
                        <VehicleSearchField
                          placeholder={t('rentals.search_vehicle')}
                          onSelect={v => handleAssignVehicle(inv.id, v.id)}
                        />
                        <button
                          onClick={() => setAssigningId(null)}
                          className="text-ink-300 hover:text-ink-700 p-1 rounded">
                          <X size={13} />
                        </button>
                      </div>
                    ) : inv.registration_number ? (
                      <button
                        onClick={() => setAssigningId(inv.id)}
                        className="font-mono text-ink-900 hover:text-sage-700 text-xs font-semibold">
                        {inv.registration_number}
                      </button>
                    ) : (
                      <button
                        onClick={() => setAssigningId(inv.id)}
                        className="text-left text-xs text-rose-700 hover:text-sage-700 underline underline-offset-2">
                        {t('rentals.assign_vehicle')}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-500 text-xs whitespace-nowrap">
                    {formatDatetime(inv.rental_start)}<br/>→ {formatDatetime(inv.rental_end)}
                  </td>
                  <td className="px-4 py-3 text-ink-700 truncate" title={inv.renter_name || ''}>{inv.renter_name || '—'}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-ink-900">{fmt(Number(inv.payout))}</td>
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const { sum, diff } = invoiceDiff(inv);
                      if (Math.abs(diff) < 0.01) return null;
                      const sign = diff > 0 ? '+' : '';
                      return (
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500"
                          title={`Sum af komponenter (${fmt(sum)}) stemmer ikke med payout (${fmt(Number(inv.payout))}). Difference: ${sign}${fmt(diff)} DKK`}
                        />
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const bal = balances[inv.id];
                      const neutral = 'text-xs px-2 py-0.5 rounded font-medium bg-cream-100 text-ink-700 whitespace-nowrap';
                      if (inv.cancelled) return <span className="text-xs text-ink-300">—</span>;
                      if (inv.invoice_number == null) {
                        if (!hasEnded(inv)) {
                          const started = !!inv.rental_start && new Date(inv.rental_start.replace(' ', 'T')) <= new Date();
                          return <span className={neutral}>{t(started ? 'rentals.badge_ongoing' : 'rentals.badge_upcoming')}</span>;
                        }
                        // Lejen er slut uden faktura: enten ikke hentet endnu, eller en rettidig aflysning
                        // som kun brugeren kan afgøre — derfor knappen, med bekræftelse i to trin.
                        const confirming = confirmCancelId === inv.id;
                        return (
                          <span className="inline-flex flex-col items-center gap-1">
                            <span className="text-xs px-2 py-0.5 rounded font-medium bg-amber-100 text-amber-700 whitespace-nowrap"
                              title={t('rentals.badge_no_invoice_title')}>{t('rentals.badge_no_invoice')}</span>
                            <button
                              type="button"
                              title={t('rentals.mark_cancelled_title')}
                              onClick={e => {
                                e.stopPropagation();
                                if (confirming) handleMarkCancelled(inv.id);
                                else setConfirmCancelId(inv.id);
                              }}
                              className={`text-[11px] underline underline-offset-2 ${confirming ? 'text-rose-700 font-medium' : 'text-ink-500 hover:text-ink-900'}`}
                            >
                              {t(confirming ? 'rentals.mark_cancelled_confirm' : 'rentals.mark_cancelled')}
                            </button>
                          </span>
                        );
                      }
                      if (!bal || bal.received === 0) {
                        return <span className={neutral}>{t('rentals.badge_unpaid', { days: bal?.days_since_end ?? '—' })}</span>;
                      }
                      // Strikt nul-tolerance: kun præcis match er OK
                      const diff = bal.balance; // received - payout
                      if (diff === 0) {
                        return <span className="text-xs px-2 py-0.5 rounded font-medium bg-sage-100 text-sage-700"
                          title={`Modtaget ${fmt(bal.received)} af ${fmt(bal.payout)} DKK`}>Stemmer</span>;
                      }
                      if (bal.status === 'pending') {
                        const vars = { amount: fmt(Math.abs(diff)), days: bal.days_since_end ?? '—' };
                        return (
                          <span className={neutral}
                            title={t('rentals.balance_pending_title', vars)}>
                            {t('rentals.balance_pending', vars)}
                          </span>
                        );
                      }
                      // diff < 0 = mangler (rød), diff > 0 = overskud (gul)
                      const cls = diff < 0 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700';
                      const label = diff < 0 ? `Mangler ${fmt(Math.abs(diff))}` : `+${fmt(diff)}`;
                      return (
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${cls}`}
                          title={`Modtaget ${fmt(bal.received)} af ${fmt(bal.payout)} DKK`}>
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    {confirmDeleteGm === inv.id ? (
                      <span className="flex items-center gap-2 justify-end">
                        <button onClick={() => handleDeleteInvoice(inv.id)} className="text-xs text-rose-700 hover:underline font-medium">{t('rentals.delete_confirm')}</button>
                        <button onClick={() => setConfirmDeleteGm(null)} className="text-ink-300 hover:text-ink-700">
                          <X size={13} />
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteGm(inv.id)}
                        className="p-1 rounded text-ink-300 hover:text-rose-700 hover:bg-rose-100 transition-colors"
                        aria-label={t('rentals.delete_confirm')}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="text-xs font-medium text-ink-700">
              <tr>
                <td colSpan={3} className="px-4 py-3">{t('fleet.total')} ({importedInvoices.length} {t('rentals.count')})</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-ink-900">{fmt(gmTotalPayout)}</td>
                <td />
                <td />
                <td />
              </tr>
              <tr>
                <td colSpan={3} className="px-4 pb-3 text-ink-500 font-normal">
                  {t('rentals.vat_summary', { sales: fmt(gmTotalSalesVat), purchase: fmt(gmTotalPurchaseVat) })}
                </td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        )}
        {rentalsTotalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100 text-sm text-ink-500">
            <span>{t('pagination.showing', { from: (rentalsPage - 1) * rentalsPageSize + 1, to: Math.min(rentalsPage * rentalsPageSize, importedInvoices.length), total: importedInvoices.length })}</span>
            <Pagination page={rentalsPage} totalPages={rentalsTotalPages} onChange={setRentalsPage} />
          </div>
        )}
      </div>

      {showManualModal && (
        <ManualBookingModal onSaved={() => { setShowManualModal(false); loadInvoices(); }} onClose={() => setShowManualModal(false)} />
      )}
      {showImportModal && (
        <ImportModal onImported={loadInvoices} onClose={() => setShowImportModal(false)} />
      )}
      {viewingInvoice && (
        <BookingDetailModal
          invoice={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
          onSave={updated => { setViewingInvoice(updated); loadInvoices(); }}
        />
      )}
    </main>
  );
}
